import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useAuth } from "../../../contexts/AuthContext";
import { users as usersAPI } from "../../../lib/database";
import { decryptPassword } from "../../../lib/password";

const TeamMembersTab = ({
  teamMembers,
  clusters,
  teams,
  onAddMember,
  onUpdateMember,
  onDeleteMember,
  onChangePassword,
  canChangePassword = false,
  generatedPassword,
  newUserUniqueId,
  onClearPassword,
}) => {
  const { user } = useAuth();
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    cluster_id: "",
    role: "",
    team_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedClusterFilter, setSelectedClusterFilter] = useState("all");
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("all");
  const [changePasswordMemberId, setChangePasswordMemberId] = useState(null);
  const [changePasswordNew, setChangePasswordNew] = useState("");
  const [changePasswordConfirm, setChangePasswordConfirm] = useState("");
  const [changePasswordSubmitting, setChangePasswordSubmitting] =
    useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");
  const [showingPasswordId, setShowingPasswordId] = useState(null);
  const [memberPasswords, setMemberPasswords] = useState({});
  const [loadingPassword, setLoadingPassword] = useState(null);

  useEffect(() => {
    if (isAddingMember && !editingMemberId) {
      const selectedProject = localStorage.getItem("selectedProject");
      const projectId =
        selectedProject && selectedProject !== "all"
          ? selectedProject
          : user?.project_id || null;
      const projectClusters =
        clusters?.filter((c) => c.project_id === projectId) || [];
      setFormData({
        name: "",
        cluster_id: projectClusters[0]?.id || "",
        role: "",
        team_id: "",
      });
    }
  }, [isAddingMember, editingMemberId, user, clusters]);

  const roleOptions = [
    { value: "worker", label: "Worker" },
    { value: "cluster_manager", label: "Cluster Manager" },
  ];

  const clusterOptions =
    clusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || [];

  const getTeamsForCluster = (clusterId) => {
    return teams?.filter((t) => t.cluster_id === clusterId) || [];
  };

  const teamOptions = formData.cluster_id
    ? getTeamsForCluster(formData.cluster_id).map((team) => ({
        value: team.id,
        label: team.name,
      }))
    : [];

  const filteredMembers =
    teamMembers
      ?.filter((member) => {
        if (selectedClusterFilter !== "all") {
          if (member.cluster_id !== selectedClusterFilter) return false;
        }
        if (selectedTeamFilter !== "all") {
          if (member.team_id !== selectedTeamFilter) return false;
        }
        return true;
      })
      .filter((member) => member.category === "staff") || [];

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData?.cluster_id) {
      newErrors.cluster_id = "Cluster is required";
    }
    if (!formData?.role) {
      newErrors.role = "Role is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setLoading(true);
        // Normalize team_id: convert empty string to null, keep valid UUIDs
        const normalizedTeamId =
          formData.team_id && formData.team_id.trim() !== ""
            ? formData.team_id
            : null;

        if (editingMemberId) {
          await onUpdateMember(editingMemberId, {
            name: formData.name,
            cluster_id: formData.cluster_id,
            role: formData.role,
            team_id: normalizedTeamId, // Pass normalized team_id to handle team membership
          });
          setEditingMemberId(null);
        } else {
          await onAddMember({
            name: formData.name,
            cluster_id: formData.cluster_id,
            role: formData.role,
            team_id: normalizedTeamId,
          });
          setIsAddingMember(false);
        }
        setFormData({
          name: "",
          cluster_id: "",
          role: "",
          team_id: "",
        });
        setErrors({});
        onClearPassword();
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (memberId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this team member? This action cannot be undone.",
      )
    ) {
      try {
        setDeletingId(memberId);
        await onDeleteMember(memberId);
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleEdit = (member) => {
    // Get the member's current cluster_id and team_id
    // cluster_id might be null, but we can get it from teams.cluster_id if team_id exists
    let memberClusterId = member.cluster_id || "";
    const memberTeamId = member.team_id || "";

    // If cluster_id is null but team_id exists, get cluster_id from teams relationship
    if (!memberClusterId && memberTeamId && member.teams?.cluster_id) {
      memberClusterId = member.teams.cluster_id;
    }

    // Validate that the team_id belongs to the cluster_id
    // If not, clear the team_id
    let validTeamId = memberTeamId;
    if (memberClusterId && memberTeamId) {
      const teamsForCluster = getTeamsForCluster(memberClusterId);
      const teamExists = teamsForCluster.some((t) => t.id === memberTeamId);
      if (!teamExists) {
        validTeamId = "";
      }
    }

    setFormData({
      name: member.name || "",
      cluster_id: memberClusterId,
      role: member.role || "",
      team_id: validTeamId,
    });
    setEditingMemberId(member.id);
    setIsAddingMember(false);
  };

  const handleCancel = () => {
    setIsAddingMember(false);
    setEditingMemberId(null);
    setFormData({
      name: "",
      cluster_id: "",
      role: "",
      team_id: "",
    });
    setErrors({});
    onClearPassword();
  };

  const getMemberTeam = (member) => {
    if (member?.team_id) {
      return teams?.find((t) => t.id === member.team_id);
    }
    return null;
  };

  const handleOpenChangePassword = (memberId) => {
    setChangePasswordMemberId(memberId);
    setChangePasswordNew("");
    setChangePasswordConfirm("");
    setChangePasswordError("");
  };

  const handleShowPassword = async (memberId) => {
    if (memberPasswords[memberId] !== undefined) {
      setShowingPasswordId(showingPasswordId === memberId ? null : memberId);
      return;
    }
    try {
      setLoadingPassword(memberId);
      const encrypted = await usersAPI.getPasswordById(memberId);
      const plain = encrypted ? decryptPassword(encrypted) : null;
      setMemberPasswords((prev) => ({ ...prev, [memberId]: plain }));
      setShowingPasswordId(memberId);
    } catch (err) {
      console.error("Error fetching password:", err);
      setMemberPasswords((prev) => ({ ...prev, [memberId]: null }));
      setShowingPasswordId(memberId);
    } finally {
      setLoadingPassword(null);
    }
  };

  const handleCloseChangePassword = () => {
    setChangePasswordMemberId(null);
    setChangePasswordNew("");
    setChangePasswordConfirm("");
    setChangePasswordError("");
  };

  const handleSubmitChangePassword = async () => {
    if (!changePasswordMemberId || !onChangePassword) return;
    if (!changePasswordNew?.trim()) {
      setChangePasswordError("New password is required");
      return;
    }
    if (changePasswordNew.trim().length < 6) {
      setChangePasswordError("Password must be at least 6 characters");
      return;
    }
    if (changePasswordNew !== changePasswordConfirm) {
      setChangePasswordError("Passwords do not match");
      return;
    }
    try {
      setChangePasswordError("");
      setChangePasswordSubmitting(true);
      await onChangePassword(changePasswordMemberId, changePasswordNew.trim());
      handleCloseChangePassword();
    } catch (err) {
      setChangePasswordError(err.message || "Failed to change password");
    } finally {
      setChangePasswordSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">
            Team Members
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage staff members and their assignments
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => {
            setIsAddingMember(true);
            setEditingMemberId(null);
            setFormData({
              name: "",
              cluster_id: "",
              role: "",
              team_id: "",
            });
            setErrors({});
          }}
        >
          Add Member
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Filter by Cluster"
            options={[
              { value: "all", label: "All Clusters" },
              ...clusterOptions,
            ]}
            value={selectedClusterFilter}
            onChange={(value) => {
              setSelectedClusterFilter(value);
              setSelectedTeamFilter("all");
            }}
            searchable
          />
          <Select
            label="Filter by Team"
            options={[
              { value: "all", label: "All Teams" },
              ...(selectedClusterFilter !== "all"
                ? getTeamsForCluster(selectedClusterFilter).map((team) => ({
                    value: team.id,
                    label: team.name,
                  }))
                : teams?.map((team) => {
                    const cluster = clusters?.find(
                      (c) => c.id === team.cluster_id,
                    );
                    return {
                      value: team.id,
                      label: `${team.name} (${cluster?.name || "N/A"})`,
                    };
                  }) || []),
            ]}
            value={selectedTeamFilter}
            onChange={(value) => setSelectedTeamFilter(value)}
            searchable
          />
        </div>
      </div>

      {/* Password Display Modal */}
      {(generatedPassword || newUserUniqueId) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Member Created Successfully
              </h3>
              <div className="space-y-3">
                {newUserUniqueId && (
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      User ID
                    </label>
                    <div className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm">
                      {newUserUniqueId}
                    </div>
                  </div>
                )}
                {generatedPassword && (
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      Password
                    </label>
                    <div className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm">
                      {generatedPassword}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Please save this password. It will not be shown again.
                    </p>
                  </div>
                )}
              </div>
              <Button
                variant="default"
                className="w-full mt-6"
                onClick={onClearPassword}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {(isAddingMember || editingMemberId) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {editingMemberId ? "Edit Team Member" : "Add Team Member"}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={loading}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Name"
                type="text"
                placeholder="Enter member name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors?.name) setErrors({ ...errors, name: "" });
                }}
                error={errors?.name}
                required
              />

              <Select
                label="Cluster"
                options={clusterOptions}
                value={formData.cluster_id || ""}
                onChange={(value) => {
                  const newClusterId = value || "";
                  // When cluster changes, validate if current team_id is still valid
                  let newTeamId = "";
                  if (newClusterId && formData.team_id) {
                    const teamsForNewCluster = getTeamsForCluster(newClusterId);
                    const teamStillValid = teamsForNewCluster.some(
                      (t) => t.id === formData.team_id,
                    );
                    if (teamStillValid) {
                      newTeamId = formData.team_id;
                    }
                  }
                  setFormData({
                    ...formData,
                    cluster_id: newClusterId,
                    team_id: newTeamId,
                  });
                  if (errors?.cluster_id)
                    setErrors({ ...errors, cluster_id: "" });
                }}
                error={errors?.cluster_id}
                placeholder="Select cluster"
                required
              />

              <Select
                label="Role"
                options={roleOptions}
                value={formData.role}
                onChange={(value) => {
                  setFormData({ ...formData, role: value });
                  if (errors?.role) setErrors({ ...errors, role: "" });
                }}
                error={errors?.role}
                placeholder="Select role"
                required
              />

              <Select
                label="Team"
                options={[{ value: "", label: "— No Team —" }, ...teamOptions]}
                value={formData.team_id || ""}
                onChange={(value) => {
                  setFormData({ ...formData, team_id: value || "" });
                }}
                placeholder={
                  formData.cluster_id ? "Select team" : "Select a cluster first"
                }
                searchable
                disabled={!formData.cluster_id}
              />

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={loading}
                  disabled={loading}
                >
                  {editingMemberId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changePasswordMemberId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                Change Password
              </h2>
              <button
                onClick={handleCloseChangePassword}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={changePasswordSubmitting}
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="New Password"
                type="password"
                value={changePasswordNew}
                onChange={(e) => setChangePasswordNew(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <Input
                label="Confirm Password"
                type="password"
                value={changePasswordConfirm}
                onChange={(e) => setChangePasswordConfirm(e.target.value)}
                placeholder="Confirm new password"
                required
              />
              {changePasswordError && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {changePasswordError}
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCloseChangePassword}
                  disabled={changePasswordSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSubmitChangePassword}
                  loading={changePasswordSubmitting}
                  disabled={
                    !changePasswordNew?.trim() ||
                    !changePasswordConfirm?.trim() ||
                    changePasswordSubmitting
                  }
                >
                  Update Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {filteredMembers?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredMembers.map((member) => {
              const cluster = clusters?.find((c) => c.id === member.cluster_id);
              const team = getMemberTeam(member);
              return (
                <div
                  key={member.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon
                            name="User"
                            size={20}
                            className="text-primary"
                          />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-base font-semibold text-foreground truncate">
                            {member.name || "Unnamed User"}
                          </h4>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.unique_id || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-muted-foreground ml-0 md:ml-13">
                        <div className="flex items-center gap-2">
                          <Icon
                            name="MapPin"
                            size={14}
                            className="flex-shrink-0"
                          />
                          <span className="truncate">
                            {cluster?.name || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon
                            name="UserCog"
                            size={14}
                            className="flex-shrink-0"
                          />
                          <span
                            className={
                              team
                                ? "text-foreground font-medium truncate"
                                : "text-muted-foreground truncate"
                            }
                          >
                            {team ? team.name : "No Team"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium whitespace-nowrap">
                            {member.role === "super_admin" ||
                            member.role === "project_admin"
                              ? "Admin"
                              : member.role === "cluster_manager"
                                ? "Cluster Manager"
                                : member.role?.charAt(0).toUpperCase() +
                                    member.role?.slice(1) || "Staff"}
                          </span>
                        </div>
                      </div>
                      {canChangePassword &&
                        showingPasswordId === member.id &&
                        memberPasswords[member.id] !== undefined && (
                          <div className="mt-3 ml-0 md:ml-13 p-3 bg-muted rounded-lg border border-border">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                  Password
                                </p>
                                <p className="text-sm font-mono font-medium text-foreground">
                                  {memberPasswords[member.id] ??
                                    "Cannot display (stored securely)"}
                                </p>
                              </div>
                              {memberPasswords[member.id] && (
                                <button
                                  onClick={() =>
                                    navigator.clipboard.writeText(
                                      memberPasswords[member.id],
                                    )
                                  }
                                  className="p-2 hover:bg-background rounded-lg transition-colors"
                                  title="Copy to clipboard"
                                >
                                  <Icon
                                    name="Copy"
                                    size={16}
                                    className="text-muted-foreground"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                    <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                      {canChangePassword && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName={
                            showingPasswordId === member.id ? "EyeOff" : "Eye"
                          }
                          onClick={() => handleShowPassword(member.id)}
                          loading={loadingPassword === member.id}
                          disabled={loadingPassword === member.id}
                          title={
                            showingPasswordId === member.id
                              ? "Hide Password"
                              : "Show Password"
                          }
                        >
                          {showingPasswordId === member.id ? "Hide" : "Show"}{" "}
                          Password
                        </Button>
                      )}
                      {canChangePassword && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Key"
                          onClick={() => handleOpenChangePassword(member.id)}
                          title="Change Password"
                        >
                          Change Password
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit2"
                        onClick={() => handleEdit(member)}
                        disabled={deletingId === member.id}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(member.id)}
                        loading={deletingId === member.id}
                        disabled={deletingId === member.id}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Icon
              name="Users"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground">
              No team members found. Click "Add Member" to create one.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMembersTab;
